import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Layout, Menu, Button, theme, Avatar, Dropdown, Space, Breadcrumb, Divider, Typography, Select, Tag } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  ScheduleOutlined,
  CarOutlined,
  SolutionOutlined,
  SettingOutlined,
  LogoutOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
  FolderOpenOutlined,
  CloseOutlined,
  MenuOutlined,
  BankOutlined,
  CalendarOutlined,
  MoreOutlined,
  SwapOutlined,
  HomeFilled,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../actions/authActions';
import { useLocation, useNavigate } from 'react-router-dom';
import SessionTimer from './sessionTimer';
import ConfirmModal from './confirmModal';
import NotFound from './notFound';
import { userActions } from '../actions/userActions';
import SubscriptionBanner from './subscriptionBanner';
import { subscriptionActions } from '../actions/subscriptionActions';
import { tenantActions } from '../actions/tenantActions';
import { branchActions } from '../actions/branchActions';
const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {

  const [collapsed, setCollapsed] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false);
  const [subscriptionModalData, setSubscriptionModalData] = useState<{ expired: boolean; endDate?: string | null }>({ expired: false, endDate: null });
  const location = useLocation();
  const auth = JSON.parse(localStorage.getItem("AUTHDATA"));
  const role = auth?.role;

  const profile = useSelector((state: any) => state.getProfileReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authPresent = Boolean(localStorage.getItem('AUTHDATA'));
  const shouldHideMenu = authPresent && (!profile || (profile.isLoading === true) && !profile.profileData);
  const { token: { colorBgContainer, borderRadiusLG }, } = theme.useToken();
  const subscriptionStatus = useSelector((state: any) => state.userSubscriptionStatusReducer);

  // Tenant switching state (header — Super Admin only)
  const headerTenantList = useSelector((state: any) => state.getHeaderTenantListReducer);
  const switchTenantState = useSelector((state: any) => state.switchTenantReducer);

  const authData = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('AUTHDATA') || '{}'); } catch { return {}; }
  }, []);
  const isSuperAdmin = authData?.role === 'SUPER_ADMIN';
  const isAdmin = authData?.role === 'ADMIN';
  const isBranchEnabled = authData?.isBranchEnabled || false;
  const currentTenantId = authData?.tenantId || '';
  const currentTenantName = authData?.tenantName || '';

  const [activeTenantId, setActiveTenantId] = useState<string>(
    String(authData?.lastLoginTenantId || authData?.tenantId || '')
  );
  const activeTenantName = authData?.tenantName || '';

  // Use fresh data from profile API when available, fallback to localStorage
  const liveAvailableTenants = profile?.profileData?.availableTenants || authData?.availableTenants || [];
  const liveBranchEnabled = profile?.profileData?.isBranchEnabled ?? authData?.isBranchEnabled ?? false;
  const liveTenantName = profile?.profileData?.tenantName || authData?.tenantName || '';

  const handleLogout = () => {
    localStorage.removeItem('AUTHDATA');
    dispatch(authActions.logout());
    navigate('/login', { replace: true });
  };


  useEffect(() => {
    const data = localStorage.getItem('AUTHDATA');
    if (data) {
      const profileData = JSON.parse(String(data));
      dispatch(userActions.getProfileRequest({ id: profileData?.id }));
      dispatch(subscriptionActions.getUserSubscriptionStatusRequest());
    }
  }, [])

  // Fetch active tenant list for super admin header switcher (all active tenants including branches)
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(tenantActions.getHeaderTenantList({ pageNumber: 1, pageSize: 500, isActive: true }));
    }
  }, [isSuperAdmin]);

  // Sync AUTHDATA with fresh profile data (availableTenants, isBranchEnabled, tenantName)
  useEffect(() => {
    const profileData = profile?.profileData;
    if (profileData && (profileData.availableTenants || profileData.isBranchEnabled !== undefined)) {
      try {
        const current = JSON.parse(localStorage.getItem('AUTHDATA') || '{}');
        let changed = false;
        if (profileData.availableTenants && JSON.stringify(current.availableTenants) !== JSON.stringify(profileData.availableTenants)) {
          current.availableTenants = profileData.availableTenants;
          changed = true;
        }
        if (profileData.isBranchEnabled !== undefined && current.isBranchEnabled !== profileData.isBranchEnabled) {
          current.isBranchEnabled = profileData.isBranchEnabled;
          changed = true;
        }
        if (profileData.tenantName && current.tenantName !== profileData.tenantName) {
          current.tenantName = profileData.tenantName;
          changed = true;
        }
        if (changed) {
          localStorage.setItem('AUTHDATA', JSON.stringify(current));
        }
      } catch (_) { /* ignore */ }
    }
  }, [profile]);

  // Handle switch tenant success — update AUTHDATA with new token and reload
  useEffect(() => {
    if (switchTenantState?.isSuccess && switchTenantState?.data) {
      const data = switchTenantState.data;
      const currentAuth = JSON.parse(localStorage.getItem('AUTHDATA') || '{}');
      const updatedAuth = {
        ...currentAuth,
        id: data.id || currentAuth.id,
        tenantId: data.tenantId,
        tenantName: data.tenantName || currentAuth.tenantName,
        lastLoginTenantId: data.tenantId,
        authToken: data.authToken || currentAuth.authToken,
        tokenExpiry: data.tokenExpiry || currentAuth.tokenExpiry,
        isBranchEnabled: data.isBranchEnabled || false,
        role: data.role || currentAuth.role,
      };
      localStorage.setItem('AUTHDATA', JSON.stringify(updatedAuth));
      // Full page reload to refresh all API contexts with new tenant
      window.location.href = '/dashboard';
    }
  }, [switchTenantState]);

  const handleTenantSwitch = useCallback((tenantId: string) => {
    if (tenantId && tenantId !== activeTenantId) {
      dispatch(branchActions.switchTenant({ tenantId }));
    }
  }, [activeTenantId, dispatch]);

  const allMenuItems = useMemo(() => ([
    { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
    { key: '/users', icon: <TeamOutlined />, label: 'Users' },
    { key: '/enrollment', icon: <ScheduleOutlined />, label: 'Enrollment' },
    { key: '/services', icon: <SolutionOutlined />, label: 'Services' },
    { key: '/categories', icon: <FolderOpenOutlined />, label: 'Category' },
    { key: '/batches', icon: <ScheduleOutlined />, label: 'Batches' },
    { key: '/vehicles', icon: <CarOutlined />, label: 'Vehicles' },
    { key: '/expenses', icon: <DollarOutlined />, label: 'Accounting' },
    { key: '/queries', icon: <QuestionCircleOutlined />, label: 'Queries' },
    { key: '/tenants', icon: <BankOutlined />, label: 'Tenants' },
    { key: '/settings', icon: <SettingOutlined />, label: 'Settings' },
  ]), []);

  const breadcrumbItems = useMemo(() => {
    const path = location.pathname || '/dashboard';
    const parts = path.split('/').filter(Boolean);
    const items: { key: string; label: string; icon?: React.ReactNode }[] = [];
    if (parts.length > 0) {
      const firstPath = `/${parts[0]}`;
      const match = allMenuItems.find(m => m.key === firstPath);
      if (match) {
        items.push({ key: match.key, label: match.label, icon: match.icon });
      } else {
        items.push({ key: firstPath, label: parts[0].charAt(0).toUpperCase() + parts[0].slice(1) });
      }
    }
    if (parts.length > 1) {
      const second = parts[1];
      const actionLabel = second.match(/^[0-9a-fA-F\-]{6,}$/) ? 'View' : (second.charAt(0).toUpperCase() + second.slice(1));
      items.push({ key: `${parts[0]}/${parts[1]}`, label: actionLabel });
    }

    return items;
  }, [location.pathname, allMenuItems]);


  const allowedRoutes = useMemo(() => {
    const subscription = profile?.profileData?.currentSubscription;
    const plan = subscription?.plan || subscription?.plan_id;
    const planRoutesRaw = plan?.route || plan?.routes;

    if (!Array.isArray(planRoutesRaw) || planRoutesRaw.length === 0) return null;
    const normalized = planRoutesRaw.map((r: any) => {
      const route = typeof r === 'string' ? r : (r?.route || r?.path || '');
      if (!route) return '';
      return route.startsWith('/') ? route : `/${route}`;
    }).filter(Boolean);
    return Array.from(new Set(normalized));
  }, [profile]);

  const menuItems = useMemo(() => {

    if (role === 'SUPER_ADMIN') {
      return [...allMenuItems];
    }

    let items = [...allMenuItems];

    // Apply allowedRoutes filtering
    if (Array.isArray(allowedRoutes) && allowedRoutes.length > 0) {
      items = items.filter((item) =>
        allowedRoutes.some((ar) => item.key === ar || item.key.startsWith(ar))
      );
    }
    items = items.filter((item) => item.key !== '/tenants');
    if (role === 'ADMIN' && auth?.tenantId) {
      const hasTenantMenu = items.some(i => i.key?.startsWith('/tenants'));

      if (!hasTenantMenu) {
        items.push({
          key: `/tenants/view`,
          label: 'My Tenant',
          icon: <HomeFilled />, // optional
        });
      } else {
        items = items.map((item) =>
          item.key?.startsWith('/tenants')
            ? {
              ...item,
              key: `/tenants/view`,
              label: 'My Tenant',
            }
            : item
        );
      }
    }
    if (role === 'STAFF') {
      items = items.filter(
        (item) =>
          !['/settings', '/batches', '/queries'].includes(item.key)
      );
    }

    return items;
  }, [allMenuItems, profile, allowedRoutes]);

  const isRouteAllowed = useMemo(() => {
    // SUPER_ADMIN and ADMIN always have access to all routes
    if (isSuperAdmin || isAdmin) return true;
    if (!Array.isArray(allowedRoutes) || allowedRoutes.length === 0) return true;
    const current = location.pathname || '/';
    return allowedRoutes.some((ar) => current === ar || current.startsWith(ar + '/') || current.startsWith(ar));
  }, [allowedRoutes, location.pathname, isSuperAdmin, isAdmin]);

  const userMenuItems = [
    { key: 'profile', label: 'My Profile', icon: <UserOutlined /> },
    { key: 'logout', label: 'Logout', icon: <LogoutOutlined /> },
  ];

  const onUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile') { navigate('/profile'); }
    if (key === 'logout') { handleLogout(); }
  };

  const handleRenewPlan = () => {
    setSubscriptionModalVisible(false);
    navigate('/subscriptions');
  };

  const handleViewPlans = () => {
    setSubscriptionModalVisible(false);
    navigate('/pricing');
  };

  const handleCloseSubscriptionModal = () => {
    setSubscriptionModalVisible(false);
  };

  useEffect(() => {
    if (profile && profile?.profileData) {
      const image = profile?.profileData?.profileImage;
      if (image?.buffer) {
        const formattedUrl = `data:${image.mimetype || 'image/png'};base64,${image.buffer}`;
        setAvatarUrl(formattedUrl);
      }
    }
  }, [profile]);

  useEffect(() => {
    try {
      const sub = profile?.profileData?.currentSubscription;
      if (!sub) {
        return;
      }
      const endDateStr = sub.endDate || sub.expiryDate || null;
      if (!endDateStr) return;
      const end = new Date(endDateStr);
      if (Number.isNaN(end.getTime())) return;
      const now = new Date();
      const msInDay = 1000 * 60 * 60 * 24;
      const daysLeft = Math.ceil((end.getTime() - now.getTime()) / msInDay);
      if (end.getTime() <= now.getTime()) {
        setSubscriptionModalData({ expired: true, endDate: end.toISOString() });
        setSubscriptionModalVisible(true);
      } else if (daysLeft <= 7) {
        setSubscriptionModalData({ expired: false, endDate: end.toISOString() });
        setSubscriptionModalVisible(true);
      }
    } catch (err) {
    }
  }, [profile]);

  return (
    <Layout className="driveMynt-layout" >
      <Sider width={230} trigger={null} collapsed={collapsed} theme="dark" className="app-sider">
        <div className="sider-inner">
          <div className="sidebar-logo">
            <div className="sidebar-logo-left">
              {!collapsed ? (
                <div className="sidebar-logo-badge" onClick={() => navigate('/')}>
                  <Avatar className="sidebar-logo-avatar" size={40}>
                    <CarOutlined size={40} className='sidebar-logo-icon' />
                  </Avatar>
                  <div className="sidebar-logo-text">
                    <div className="sidebar-logo-main">DriveMynt</div>
                  </div>
                </div>
              ) : (
                <div className="sidebar-logo-collapsed">
                  <MenuOutlined onClick={() => setCollapsed(false)} className="menu-toggle-icon" />
                </div>
              )}
            </div>
            <div className="sidebar-logo-right">
              <Button type="text" className={`header-toggle-btn ${collapsed ? 'hidden' : ''}`} icon={<CloseOutlined className="menu-close-icon" />} onClick={() => setCollapsed(!collapsed)}
                aria-label="Toggle sidebar" />
            </div>
          </div>
          <div className='sidebar-divider'> <Divider className="sidebar-divider-line" /> </div>
          <div className="menu-scroll">
            {shouldHideMenu ? (
              <div style={{ height: 200 }} />
            ) : (
              <Menu theme="dark" mode="inline" selectedKeys={[location.pathname]} items={menuItems} onClick={({ key }) => {
                if (key === '/tenants/view' && role === 'ADMIN') {
                  navigate('/tenants/view', {
                    state: { tenantId: auth?.tenantId },
                  });
                } else {
                  navigate(key);
                }
              }} />
            )}
          </div>
          <div className="sidebar-profile">
            <Dropdown menu={{ items: userMenuItems, onClick: onUserMenuClick }} placement="topLeft">
              <div className="sidebar-profile-inner" role="button" tabIndex={0} onKeyDown={() => { }}>
                <Avatar size={40} src={avatarUrl || undefined} icon={<UserOutlined />} />
                {!collapsed && (
                  <div className="sidebar-profile-meta">
                    <div className="sidebar-profile-name">
                      {((profile?.profileData?.firstName || '') + ' ' + (profile?.profileData?.lastName || '')).trim() || ''}
                    </div>
                    <div className="sidebar-profile-email" style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
                      {profile?.profileData?.role?.roleName || ''}
                    </div>
                  </div>
                )}
                {!collapsed && (
                  <MoreOutlined className="sidebar-profile-menu-icon" />
                )}
              </div>
            </Dropdown>
          </div>
        </div>
      </Sider>
      <Layout className={collapsed ? 'layout-collapsed' : 'layout-expanded'}>
        <Header className="driveMynt-header">
          <div className="breadcrumb-wrapper">
            <Breadcrumb>
              {breadcrumbItems.map((it) => (
                <Breadcrumb.Item key={it.key}>
                  <span className="breadcrumb-item-content">
                    {it.icon ? <span className="breadcrumb-icon">{it.icon}</span> : null}
                    <span>{it.label}</span>
                  </span>
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
          </div>
          <Space size="middle" align="center">
            {/* Non-SuperAdmin users with multiple available tenants: show switcher */}
            {!isSuperAdmin && liveAvailableTenants.length > 1 && (
              <Select
                value={activeTenantId || undefined}
                onChange={handleTenantSwitch}
                placeholder="Switch Tenant"
                loading={switchTenantState?.isLoading}
                disabled={switchTenantState?.isLoading}
                style={{ minWidth: 200 }}
                suffixIcon={<SwapOutlined />}
                optionLabelProp="label"
                virtual={false}
                listHeight={256}
                options={
                  liveAvailableTenants.map((t: any) => ({
                    label: t.name,
                    value: String(t._id),
                  }))
                }
                showSearch
                filterOption={(input: string, option: any) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            )}
            {/* Non-SuperAdmin users with single tenant: show name tag */}
            {!isSuperAdmin && liveAvailableTenants.length <= 1 && liveTenantName && (
              <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px', margin: 0 }}>
                <BankOutlined style={{ marginRight: 6 }} />
                {liveTenantName}
              </Tag>
            )}

            {isSuperAdmin && (
              <Select
                value={activeTenantId || undefined}
                onChange={handleTenantSwitch}
                placeholder="Switch Tenant"
                loading={headerTenantList?.isLoading || switchTenantState?.isLoading}
                disabled={switchTenantState?.isLoading}
                style={{ minWidth: 220 }}
                suffixIcon={<SwapOutlined />}
                virtual={false}
                className='header-tenant-switcher'
                options={(headerTenantList?.tenantData?.tenants || []).map((t: any) => ({
                  label: t.name,
                  value: String(t._id),
                }))}
                showSearch
              />
            )}

          </Space>
        </Header>
        {(subscriptionStatus?.data?.status) === "scheduled" || (subscriptionStatus?.data?.status) === "active" && <SubscriptionBanner />}
        <Content className="layout-content" style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
          <SessionTimer />
          {isRouteAllowed ? children : <NotFound />}
        </Content>
      </Layout>
      {role !== 'SUPER_ADMIN' && <ConfirmModal
        visible={subscriptionModalVisible}
        title={null}
        onOk={handleRenewPlan}
        onCancel={handleCloseSubscriptionModal}
        footer={null}
        className="subscription-modal"
        width={420}
        bodyStyle={{ padding: 32, textAlign: 'center' }}
        content={(
          <>
            <div className="modal-icon-wrapper">
              <div className="modal-icon-circle">
                <CalendarOutlined style={{ fontSize: 28, color: '#d94660' }} />
              </div>
            </div>

            <Typography.Title level={4} className="modal-title">
              {subscriptionModalData.expired ? 'Your Plan Has Expired' : 'Your Plan Will Expire Soon'}
            </Typography.Title>

            <Typography.Paragraph className="modal-desc">
              {subscriptionModalData.expired
                ? 'To continue using your premium features and accessing your data, please renew your subscription.'
                : `Your plan will expire on ${subscriptionModalData.endDate ? new Date(subscriptionModalData.endDate).toLocaleDateString() : 'the upcoming date'}. Please renew to avoid interruption.`}
            </Typography.Paragraph>

            <div className="modal-actions">
              <Button block onClick={handleViewPlans} className="btn-primary hover-effect">
                Update Payment
              </Button>
            </div>
          </>
        )}
      />}
    </Layout>
  );
};

export default MainLayout;